import React, { forwardRef, useEffect, useRef, useState } from "react";
import { Icons } from "@/assets";
import { cx } from "@/utils/cx";
import { containsOnlyDigits } from "@/utils/validation";

interface NumberInputFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "onBlur" | "value"> {
    name: string;
    label: string;
    value?: string;
    onChange?: (val: string) => void;
    onBlur?: (val: string) => void;
    error?: string;
    required?: boolean;
    uomValue?: string;
    uomOptions?: { label: string; value: string }[];
    onUomChange?: (val: string) => void;
    uomPlaceholder?: string;
    allowSpaces?: boolean;
}

export const NumberInputField = forwardRef<HTMLInputElement, NumberInputFieldProps>(({
    name,
    label,
    value,
    onChange,
    onBlur,
    error,
    required,
    disabled,
    className,
    uomValue,
    uomOptions = [],
    onUomChange,
    uomPlaceholder = "UOM",
    allowSpaces = false,
    placeholder,
    ...props
}, ref) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [isUomOpen, setIsUomOpen] = useState(false);
    const [localError, setLocalError] = useState("");

    const hasUom = uomOptions.length > 0;
    const selectedLabel = uomOptions.find((option) => String(option.value) === String(uomValue))?.label || uomPlaceholder;
    const resolvedError = error || localError;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsUomOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleValueChange = (nextValue: string) => {
        if (!nextValue) {
            setLocalError("");
            onChange?.("");
            return;
        }

        const isValidNumberValue = allowSpaces
            ? /^\d+(?: \d+)*$/.test(nextValue)
            : containsOnlyDigits(nextValue);

        if (!isValidNumberValue) {
            setLocalError("Enter number only");
            return;
        }

        setLocalError("");
        onChange?.(nextValue);
    };

    return (
        <div ref={wrapperRef} className="flex flex-col gap-2.5 w-full relative">
            <label htmlFor={name} className="text-base text-gray-90">
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
            </label>

            <div
                className={cx(
                    "relative w-full rounded-[10px] p-[1.5px] transition-all duration-200 ease-in-out group",
                    resolvedError
                        ? "bg-[#ff424f]"
                        : "bg-gray-30 focus-within:bg-gradient-to-r focus-within:from-[#00B4EA] focus-within:to-[#8A00FF]",
                    disabled && "opacity-60 cursor-not-allowed bg-gray-200"
                )}
            >
                <div className="relative flex items-center bg-white rounded-[8.5px] overflow-visible">
                    <input
                        ref={ref}
                        id={name}
                        name={name}
                        type="text"
                        inputMode="numeric"
                        value={value || ""}
                        disabled={disabled}
                        placeholder={placeholder}
                        onChange={(event) => handleValueChange(event.target.value)}
                        onBlur={(event) => onBlur?.(event.target.value)}
                        className={cx(
                            "w-full h-[51px] px-4 text-gray-900 text-sm placeholder:text-gray-400 outline-none bg-transparent",
                            hasUom && "pr-[104px]",
                            disabled && "cursor-not-allowed bg-gray-50",
                            className
                        )}
                        {...props}
                    />

                    {resolvedError && (
                        <div className={cx("absolute pointer-events-none flex items-center", hasUom ? "right-[94px]" : "right-4")}>
                            <Icons.ErrorIcon className="w-5 h-5 text-[#ff424f]" />
                        </div>
                    )}

                    {hasUom && (
                        <>
                            <button
                                type="button"
                                disabled={disabled}
                                onClick={() => setIsUomOpen((open) => !open)}
                                className={cx(
                                    "absolute right-2 top-1/2 -translate-y-1/2 h-[37px] min-w-[84px] max-w-[112px] px-3",
                                    "flex items-center justify-between gap-2 rounded-[8px] border border-gray-30 bg-gray-10",
                                    "text-sm text-gray-90 outline-none transition-colors",
                                    disabled ? "cursor-not-allowed" : "hover:border-gray-400"
                                )}
                            >
                                <span className="truncate">{selectedLabel}</span>
                                <Icons.ChevronDownIcon className={cx("w-4 h-4 shrink-0 text-gray-500 transition-transform", isUomOpen && "rotate-180")} />
                            </button>

                            {isUomOpen && !disabled && (
                                <div className="absolute top-[46px] right-2 min-w-[112px] max-w-[180px] bg-white border border-gray-200 rounded-[8px] shadow-lg max-h-[200px] overflow-y-auto z-50">
                                    {uomOptions.map((option) => (
                                        <button
                                            key={option.value}
                                            type="button"
                                            onMouseDown={(event) => event.preventDefault()}
                                            onClick={() => {
                                                onUomChange?.(option.value);
                                                setIsUomOpen(false);
                                            }}
                                            className={cx(
                                                "w-full px-3 py-2 text-left text-sm hover:bg-gray-50",
                                                String(option.value) === String(uomValue) ? "text-[#0a68ff] font-medium" : "text-gray-700"
                                            )}
                                        >
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {resolvedError && (
                <p className="text-xs text-error-60 font-medium">{resolvedError}</p>
            )}
        </div>
    );
});

NumberInputField.displayName = "NumberInputField";
